import {
    snapshotConfig,
    SnapshotInterface,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { Duration, formatDuration } from 'date-fns';
import { useSnapshotQueryData } from 'app/agriculture/queries/snapshots/useSnapshotQueryData';
import { filterSnapshotsMap } from 'app/agriculture/queries/snapshots/utils/filterSnapshotsMap';
import { Snapshot } from 'app/agriculture/queries/snapshots/types/Snapshot';
import { SnapshotMap } from 'app/agriculture/queries/snapshots/types/SnapshotMap';
import { useMemo } from 'react';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { useCursorTimeForSnapshotType } from 'app/agriculture/queries/snapshots/useCursorTimeForSnapshotType';

/**
 * Returns a Map of the latest snapshot for each parentId
 * @param snapshotType
 * @param shelfLifeOverride defaults to snapshotConfig.ts
 * @param cursorTimeOverride defaults to timeline cursor time
 */
export function useSnapshots<T extends SnapshotType, I extends Snapshot = SnapshotInterface<T>>({
    snapshotType,
    shelfLife: shelfLifeOverride,
    cursorTimeOverride,
}: {
    snapshotType: T;
    shelfLife?: Duration | null;
    cursorTimeOverride?: Date;
}): SnapshotMap<I> {
    const { timeZone } = useTimeZone();
    const { shelfLife: configShelfLife } = snapshotConfig[snapshotType];
    const shelfLife = shelfLifeOverride === undefined ? configShelfLife : shelfLifeOverride;

    const cursorTime = useCursorTimeForSnapshotType({ snapshotType, cursorTimeOverride });

    const snapshotQueryData = useSnapshotQueryData<T, I>({
        snapshotType,
        shelfLife,
        cursorTime,
    });

    return useMemo(
        () =>
            filterSnapshotsMap<T, I>(snapshotQueryData, {
                cursorTime,
                shelfLife,
                timeZone,
            }),
        [
            snapshotQueryData,
            // this is to catch new shelf life duration objects with the same value
            shelfLife == null ? shelfLife : formatDuration(shelfLife),
            cursorTime,
            timeZone,
        ],
    );
}
